'use client';

import { useEffect, useState } from 'react';

import { attemptChunkRecovery, getChunkErrorConfidence } from '@/utils/chunkError';
import { logger } from '@/utils/logger';

const GlobalError = ({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) => {
  const chunkErrorConfidence = getChunkErrorConfidence(error);
  const chunkError = chunkErrorConfidence !== null;
  const [isRecovering, setIsRecovering] = useState(chunkError);

  useEffect(() => {
    logger.error('[global-error] uncaught error', {
      channel: 'error-boundary',
      boundary: 'app/global-error',
      message: error?.message,
      digest: error?.digest,
      stack: error?.stack,
      isChunkError: chunkError,
      chunkErrorConfidence,
      url: globalThis.location?.href,
    });

    if (chunkError) {
      setIsRecovering(attemptChunkRecovery('app/global-error'));
    }
  }, [chunkError, chunkErrorConfidence, error]);

  const handleRetry = () => {
    if (chunkError) {
      globalThis.location.reload();
      return;
    }
    reset();
  };

  const description = {
    high: 'O site foi atualizado enquanto você navegava. Recarregue a página para continuar.',
    low: 'Não foi possível carregar um recurso da página. Recarregue para tentar novamente.',
    none: 'Algo deu errado. Nossa equipe foi notificada e está trabalhando para resolver o problema.',
  }[chunkErrorConfidence ?? 'none'];

  return (
    <html lang="pt-BR">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          background: 'var(--wl-color-background, #ffffff)',
          color: 'var(--wl-color-foreground, #000000)',
        }}
      >
        {!isRecovering && (
          <main style={{ maxWidth: '420px', textAlign: 'center' }}>
            <h1 style={{ fontSize: '20px', margin: '0 0 8px' }}>
              {chunkError
                ? 'Não foi possível carregar a página'
                : 'Erro interno do servidor'}
            </h1>
            <p style={{ fontSize: '14px', lineHeight: 1.5, margin: '0 0 24px' }}>
              {description}
            </p>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                fontWeight: 600,
                color: 'var(--wl-color-primary-foreground, #ffffff)',
                background: 'var(--wl-color-primary, #0540fa)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              {chunkError ? 'Recarregar página' : 'Tentar novamente'}
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                display: 'inline-block',
                marginTop: '16px',
                fontSize: '14px',
                color: 'var(--wl-color-foreground, #000000)',
              }}
            >
              Voltar ao início
            </a>
          </main>
        )}
      </body>
    </html>
  );
};

export default GlobalError;
